/**
 * Ticket API Service
 *
 * REST client for /api/tickets — CRUD and CAB-driven generation.
 */

import api from '../utils/axios';
import type { Ticket, TicketWorkType, TicketStatus, TicketPriority, TicketSource, TicketExpertise, TicketPlatformType } from '../pages/TicketsPage';
import type { PageSize } from '../utils/pagination';

const BASE = '/tickets';

// ─── API shapes (snake_case from backend) ────────────────────────────────────

export interface ApiAssignTo {
  name: string;
  email: string;
}

export type TicketEvaluationStatus = 'pending' | 'completed' | 'failed';

interface ApiTicketSummary {
  id: string;
  project_id: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  work_type?: TicketWorkType;
  expertise: TicketExpertise;
  platform_type: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  cab_reference?: string;
  created_date: string;
  created_by: string;
  ai_evaluation_status?: TicketEvaluationStatus;
  estimated_effort?: number | string;
}

interface ApiTicket extends ApiTicketSummary {
  description: string;
  ai_reasoning?: string;
  implementation_steps?: Array<{ step: string; human_required: boolean }>;
}

export interface EvaluateResponse {
  job_id: string | null;
  ticket_ids: string[];
  message: string;
}

export interface EvaluationJobStatus {
  job_id: string;
  status: string;
  total: number;
  completed: number;
  results: Array<{
    ticket_id: string;
    status: string;
    data: {
      evaluation: string;
      reasoning: string;
      implementation_steps: Array<{ step: string; human_required: boolean }>;
    };
  }> | null;
}

// ─── Query / pagination types ─────────────────────────────────────────────────

export type SortField = 'id' | 'title' | 'status' | 'priority' | 'work_type' | 'source' | 'assign_to' | 'created_date';
export type SortDir = 'asc' | 'desc';
// Re-exported from the shared pagination utils so both TicketsPage and other
// list views can import PAGE_SIZE_OPTIONS / PageSize from a single place.
export { PAGE_SIZE_OPTIONS, type PageSize } from '../utils/pagination';

export interface TicketListParams {
  page: number;
  pageSize: PageSize;
  sortField: SortField;
  sortDir: SortDir;
  search?: string;
  status?: string[];
  priority?: string[];
  source?: string[];
  workType?: string[];
  expertise?: string[];
  view?: 'all' | 'my';
}

export interface TicketListResult {
  tickets: Ticket[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateTicketRequest {
  title: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo;
  due_date?: string;
  cab_reference?: string;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  source?: TicketSource;
  work_type?: TicketWorkType;
  expertise?: TicketExpertise;
  platform_type?: TicketPlatformType;
  assign_to?: ApiAssignTo | null;
  due_date?: string;
  cab_reference?: string;
}

export interface TranslateJobStartResult {
  job_id: string | null;
  total: number;
  message: string;
}

export interface TranslateJobStatus {
  job_id: string;
  total: number;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function mapSummary(t: ApiTicketSummary): Ticket {
  return {
    id: t.id,
    projectId: t.project_id,
    title: t.title,
    description: '',
    status: t.status,
    priority: t.priority,
    source: t.source,
    workType: t.work_type,
    expertise: t.expertise,
    platformType: t.platform_type,
    assignTo: t.assign_to ?? null,
    dueDate: t.due_date,
    cabReference: t.cab_reference,
    createdDate: t.created_date,
    createdBy: t.created_by,
    aiEvaluationStatus: t.ai_evaluation_status,
    estimatedEffort: t.estimated_effort,
  };
}

function mapTicket(t: ApiTicket): Ticket {
  return {
    ...mapSummary(t),
    description: t.description,
    aiReasoning: t.ai_reasoning,
    implementationSteps: t.implementation_steps?.map(s => ({ step: s.step, humanRequired: s.human_required })),
  };
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function listTickets(projectId: string, params: TicketListParams): Promise<TicketListResult> {
  const q = new URLSearchParams();
  q.set('page', String(params.page));
  q.set('page_size', String(params.pageSize));
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));
  params.expertise?.forEach(v => q.append('expertise', v));

  const res = await api.get<{
    tickets: ApiTicketSummary[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }>(`${BASE}/${projectId}?${q.toString()}`);

  return {
    tickets: res.data.tickets.map(mapSummary),
    total: res.data.total,
    page: res.data.page,
    pageSize: res.data.page_size,
    totalPages: res.data.total_pages,
  };
}

export async function listTicketIds(projectId: string, params: Omit<TicketListParams, 'page' | 'pageSize'>): Promise<string[]> {
  const q = new URLSearchParams();
  q.set('sort_field', params.sortField);
  q.set('sort_dir', params.sortDir);
  if (params.view) q.set('view', params.view);
  if (params.search) q.set('search', params.search);
  params.status?.forEach(v => q.append('status', v));
  params.priority?.forEach(v => q.append('priority', v));
  params.source?.forEach(v => q.append('source', v));
  params.workType?.forEach(v => q.append('work_type', v));
  params.expertise?.forEach(v => q.append('expertise', v));

  const res = await api.get<string[]>(`${BASE}/${projectId}/ids?${q.toString()}`);
  return res.data;
}

export async function getTicket(projectId: string, ticketId: string): Promise<Ticket> {
  const res = await api.get<ApiTicket>(`${BASE}/${projectId}/${ticketId}`);
  return mapTicket(res.data);
}

export async function createTicket(projectId: string, data: CreateTicketRequest): Promise<Ticket> {
  const res = await api.post<ApiTicket>(`${BASE}/${projectId}`, data);
  return mapTicket(res.data);
}

export async function updateTicket(projectId: string, ticketId: string, data: UpdateTicketRequest): Promise<Ticket> {
  const res = await api.patch<ApiTicket>(`${BASE}/${projectId}/${ticketId}`, data);
  return mapTicket(res.data);
}

export async function deleteTickets(projectId: string, ticketIds: string | string[]): Promise<number> {
  const ids = Array.isArray(ticketIds) ? ticketIds : [ticketIds];
  const res = await api.delete<{ deleted: number }>(`${BASE}/${projectId}`, {
    data: { ticket_ids: ids },
  });
  return res.data.deleted;
}

export async function getCabTicketCount(projectId: string): Promise<number> {
  const res = await api.get<{ count: number }>(`${BASE}/${projectId}/cab-count`);
  return res.data.count;
}

export async function getTicketCounts(projectId: string): Promise<{ all: number; my: number }> {
  const res = await api.get<{ all: number; my: number }>(`${BASE}/${projectId}/counts`);
  return res.data;
}

export async function generateTicketsFromCAB(projectId: string): Promise<TranslateJobStartResult> {
  const res = await api.post<TranslateJobStartResult>(
    `${BASE}/${projectId}/generate-from-cab`,
  );
  return res.data;
}

export async function getTranslateJobStatus(projectId: string): Promise<TranslateJobStatus | null> {
  const res = await api.get<TranslateJobStatus | null>(`${BASE}/${projectId}/translate/status`);
  return res.data;
}

export async function checkTranslateJob(projectId: string): Promise<{ status: string } & Record<string, any>> {
  const res = await api.get<{ status: string } & Record<string, any>>(`${BASE}/${projectId}/translate/check`);
  return res.data;
}

export async function evaluateTickets(projectId: string, ticketIds: string[]): Promise<EvaluateResponse> {
  const res = await api.post<EvaluateResponse>(`${BASE}/${projectId}/evaluate`, {
    ticket_ids: ticketIds,
  });
  return res.data;
}

export async function checkEvaluationJob(projectId: string, ticketId: string): Promise<EvaluationJobStatus> {
  const res = await api.get<EvaluationJobStatus>(`${BASE}/${projectId}/evaluate/check/${ticketId}`);
  return res.data;
}
